import React, { useState } from "react";
import cs from "classnames";
import { useApolloClient } from "@apollo/client";
import { toast } from "react-toastify";

import { GET_ROOT_WORKPLACES } from "../../../api/graphql/workplaces";
import { LocationItem } from "../../../api/grpc/workplaces/workplaces";
import { useWorkplacesClient } from "../../../api/grpc/workplaces/useWorkplacesClient";
import { WorkplaceFields } from "../../../api/graphql/workplaces";
import { useAuthContext } from "../../../lib/context/Auth/AuthContext";
import { useTimeZoneContext } from "../../../lib/context/TimeZone/TimeZoneContext";
import { useRootIdQuery } from "../../../lib/hooks/useRootIdQuery";
import { useToggleAll } from "../../../lib/hooks/useToggleAll";
import { WorkspacePicker } from "../../shared/WorkspacePicker/WorkspacePicker";
import { ToggleAllButton } from "../../shared/ToggleAllButton/ToggleAllButton";
import { Icon } from "../../shared/Icon/Icon";
import { WayFinderWorkspaceFooterPicker } from "../../WayFinder/WayFinderPicker/WayFinderWorkspaceFooterPicker";
import { WorkplaceTypes } from "../AddWorkplace/AddWorkplaceButton/AddWorkplaceButton";
import { MoveWorkspaceTreeLevel } from "./MoveWorkspaceTreeLevel";
import { tooltips } from "../../../lib/utils/tooltips";

import "./MoveWorkspacePicker.scss";

interface Props {
  closePicker: () => void;
  workspaceId: string;
  type: WorkplaceTypes;
}

interface RootDestination {
  id: string;
  __typename: "Tenant";
  name: string;
}

type MoveDestination = WorkplaceFields | RootDestination;

const hiddenWorkplaceTypes = new Set(["Room", "Desk", "Wayfinder"]);
const ROOT_PARENT_TYPE = "Tenant";

const getNearestCurrentParent = (
  path: LocationItem[],
  workspaceId: string
): LocationItem | null => {
  if (!path.length) {
    return null;
  }

  const currentWorkspaceIndex = path.findIndex(
    (item) => item.id === workspaceId
  );

  // Most responses contain the full path including the current item.
  if (currentWorkspaceIndex > 0) {
    return path[currentWorkspaceIndex - 1];
  }

  // Some responses can return only ancestors (without the current item).
  if (currentWorkspaceIndex === -1) {
    return path[path.length - 1];
  }

  return null;
};

export const MoveWorkspacePicker = ({
  closePicker,
  workspaceId,
  type,
}: Props) => {
  const isWayfinderMove = type === "Wayfinder";
  const apolloClient = useApolloClient();
  const { user } = useAuthContext();
  const { timeZone } = useTimeZoneContext();
  const { rootId } = useRootIdQuery();
  const { changeWorkspaceItemParent, getWorkplaceLocationPath } =
    useWorkplacesClient();
  const { allToggled, handleToggleAll } = useToggleAll();
  const [isMoving, setIsMoving] = useState(false);
  const tenantRootId = rootId || user?.customerid || "";
  const [selectedDestination, setSelectedDestination] =
    useState<MoveDestination | null>(null);

  const handleMoveWorkspace = async () => {
    if (isMoving) {
      return;
    }

    if (!selectedDestination) {
      toast.error("Please select a destination workspace.");
      return;
    }

    if (selectedDestination.id === workspaceId) {
      toast.error("Please select a different destination.");
      return;
    }

    if (isWayfinderMove && selectedDestination.__typename !== "Floor") {
      toast.error("Wayfinders can only be placed on Floors.");
      return;
    }

    try {
      setIsMoving(true);

      if (!user?.customerid) {
        throw new Error("User tenant information is missing.");
      }

      const {
        response: { path },
      } = await getWorkplaceLocationPath({
        onlyFree: false,
        filterTags: [],
        locationID: workspaceId,
        customerID: "",
        allDesks: false,
        ignoreBlocked: false,
        timeZone: timeZone,
        page: 1,
        pageSize: 8,
      });

      const currentParent = getNearestCurrentParent(path || [], workspaceId);
      const isRootLevelWorkspace =
        !currentParent ||
        currentParent.type.toLowerCase() === ROOT_PARENT_TYPE.toLowerCase();

      await changeWorkspaceItemParent({
        id: workspaceId,
        type,
        currentParentId: isRootLevelWorkspace
          ? rootId || user.customerid
          : currentParent.id,
        currentParentType: isRootLevelWorkspace
          ? ROOT_PARENT_TYPE
          : currentParent.type,
        newParentId: selectedDestination.id,
        newParentType: selectedDestination.__typename,
      });

      await apolloClient.refetchQueries({
        include: [GET_ROOT_WORKPLACES],
      });

      toast.success("Workspace moved successfully!");
      closePicker();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.message || "Workspace couldn't be moved, please try again!"
      );
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <WorkspacePicker
      title="Move item"
      message={tooltips.workplaces.moveWorkplace}
      description="Select destination (new location) for the item"
      closePicker={closePicker}
      sidebarClassName="MoveWorkspacePicker--mobileCentered"
      showCloseButton={false}
      footer={
        <div
          className={
            isWayfinderMove ? "MoveWorkspacePicker__footer--wayfinder" : ""
          }
        >
          <WayFinderWorkspaceFooterPicker
            desksCounter={0}
            roomsCounter={0}
            clearWorkspaces={() => {
              if (isMoving) {
                return;
              }
              setSelectedDestination(null);
              closePicker();
            }}
            handleConfirm={handleMoveWorkspace}
            clearLabel="Cancel"
            confirmLabel={isMoving ? "Moving..." : "Move"}
          >
            {isWayfinderMove ? (
              <div className="MoveWorkspacePicker__warning flex-a-center">
                <Icon icon="warning-triangle" />
                <span>Wayfinders can only be placed on Floors.</span>
              </div>
            ) : (
              <></>
            )}
          </WayFinderWorkspaceFooterPicker>
        </div>
      }
    >
      {({ rootWorkplaces }) => {
        return (
          <>
            <ToggleAllButton
              allToggled={allToggled}
              onToggleAll={handleToggleAll}
            />

            <div className="MoveWorkspacePicker__list">
              <div
                className={cs(
                  "WorkplacesTreeLevel",
                  "MoveWorkspacePicker__rootOption",
                  {
                    "WorkplacesTreeLevel--active":
                      selectedDestination?.id === tenantRootId &&
                      selectedDestination?.__typename === ROOT_PARENT_TYPE,
                  }
                )}
              >
                <div className="WorkplacesTreeLevel__wrapper">
                  <button
                    type="button"
                    className="WorkplacesTreeLevel__link"
                    onClick={() =>
                      setSelectedDestination({
                        id: tenantRootId,
                        __typename: "Tenant",
                        name: "Workspaces",
                      })
                    }
                  >
                    <h3 className="WorkplacesTreeLevel__name">Workspaces</h3>
                  </button>
                  <div
                    className="WorkplacesTreeLevel__status"
                    onClick={() =>
                      setSelectedDestination({
                        id: tenantRootId,
                        __typename: "Tenant",
                        name: "Workspaces",
                      })
                    }
                  >
                    <Icon
                      icon={
                        selectedDestination?.id === tenantRootId &&
                        selectedDestination?.__typename === ROOT_PARENT_TYPE
                          ? "radio-checked-alt"
                          : "radio-default"
                      }
                    />
                  </div>
                </div>
              </div>

              {rootWorkplaces.map((item) => {
                if (typeof item !== "object") {
                  return null;
                }

                return item
                  .filter(
                    (workplace: WorkplaceFields) =>
                      !hiddenWorkplaceTypes.has(workplace.__typename)
                  )
                  .map((workplace: WorkplaceFields) => (
                    <MoveWorkspaceTreeLevel
                      item={workplace}
                      toggleParent={() => {}}
                      key={`${workplace.id}-move-tree-root`}
                      allToggled={allToggled}
                      selectedDestinationId={selectedDestination?.id}
                      onSelectDestination={setSelectedDestination}
                    />
                  ));
              })}
            </div>
          </>
        );
      }}
    </WorkspacePicker>
  );
};
